import { NgModule, ErrorHandler, DoBootstrap, Injector, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import * as Sentry from '@sentry/browser';
import { GlobalErrorHandler } from './global-error-handler';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ApiModule, BASE_PATH } from '@api';
import { WINDOW } from './core/injection-tokens';
import { isMyLoanInfoWindow } from './core/models/myloaninfo-window';
import { provideNgxMask } from 'ngx-mask';
import { InterfaceService } from './features/popups/interface.service';
import { createCustomElement } from '@angular/elements';
import { RegistrationAppComponent } from './features/auth/components/registration-app.component';
import { RecoverUsernameComponent } from './features/auth/components/recover-username.component';
import { RequestPasswordResetComponent } from './features/auth/components/request-password-reset.component';
import { ResetPasswordComponent } from './features/auth/components/reset-password.component';
import { VerifyEmailContainer } from './features/email/verify-email.container';
import { GoGreenContainer } from './features/go-green/containers/go-green.container';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DummyRecaptchaService, GoogleRecaptchaService, RecaptchaService } from './core/services/recaptcha.service';
import { ConfigService } from './core/services/config.service';

if (isMyLoanInfoWindow(window)) {
  Sentry.init({ dsn: window.sentry_public_dsn, ...window.sentry_config, sampleRate: 0.01, sendDefaultPii: true });

  Object.entries(window.sentry_tags_context).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });
}

@NgModule({
  imports: [BrowserModule, NgxSpinnerModule, ApiModule],
  providers: [
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: WINDOW,
      useValue: window,
    },
    provideNgxMask(),
    {
      provide: BASE_PATH,
      useValue: '',
    },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: RecaptchaService,
      deps: [ConfigService],
      useFactory: (configService: ConfigService) => {
        const config = configService.getConfig();

        if (config.recaptcha_method === 'loanmanager.recaptcha.dummy') {
          return new DummyRecaptchaService();
        } else if (config.recaptcha_method === 'loanmanager.recaptcha.google') {
          return new GoogleRecaptchaService(config);
        } else {
          throw new Error('invalid recaptcha method');
        }
      },
    },
  ],
})
export class AppModule implements DoBootstrap {
  private interfaceService = inject(InterfaceService);
  private injector = inject(Injector);

  ngDoBootstrap() {
    this.interfaceService.install();

    customElements.define(
      'app-registration-app',
      createCustomElement(RegistrationAppComponent, {
        injector: this.injector,
      })
    );

    customElements.define(
      'app-recover-username',
      createCustomElement(RecoverUsernameComponent, {
        injector: this.injector,
      })
    );

    customElements.define(
      'app-request-password-reset',
      createCustomElement(RequestPasswordResetComponent, {
        injector: this.injector,
      })
    );

    customElements.define(
      'app-reset-password',
      createCustomElement(ResetPasswordComponent, {
        injector: this.injector,
      })
    );

    customElements.define(
      'app-verify-email',
      createCustomElement(VerifyEmailContainer, {
        injector: this.injector,
      })
    );

    customElements.define(
      'app-go-green',
      createCustomElement(GoGreenContainer, {
        injector: this.injector,
      })
    );
  }
}
